/* eslint-disable max-lines-per-function, complexity, max-len, no-console */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/EnhancedAuthContext';
import { supabase } from '@/lib/supabase/client';
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-logo-emblem.png';

export const SetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExchanging, setIsExchanging] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Check if we have a valid token in the URL (from invite link)
  // Updated to handle PKCE code exchange following Supabase guidance
  // IMPORTANT: Check for existing session FIRST before trying to exchange code
  // because detectSessionInUrl: true may have already processed the PKCE code
  useEffect(() => {
    const checkSession = async () => {
      setIsExchanging(true);
      setError(null);

      try {
        // FIRST: Check if we already have a session
        // With detectSessionInUrl: true, Supabase automatically processes PKCE codes
        // on page load, which consumes the one-time code. If that happened, we'll have
        // a valid session already, so check for it BEFORE trying to exchange the code.
        const {
          data: { session: existingSession }
        } = await supabase.auth.getSession();

        if (existingSession) {
          console.log(
            '[SetPasswordPage] Session already exists (likely from detectSessionInUrl auto-processing)'
          );
          setHasValidSession(true);
          setIsExchanging(false);
          // Clean up URL if there are any auth params
          const code = searchParams.get('code');
          if (code || window.location.hash) {
            window.history.replaceState({}, '', window.location.pathname);
          }
          return;
        }

        // Check for PKCE code in query params (primary method for flowType: 'pkce')
        const code = searchParams.get('code');
        const type = searchParams.get('type');

        // If we have a code (PKCE flow), exchange it for a session
        if (code) {
          console.log('[SetPasswordPage] Exchanging PKCE code for session...');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[SetPasswordPage] Code exchange error:', exchangeError);
            // Don't show error immediately - the code might have been consumed by detectSessionInUrl
            // Check one more time for a session that might have been established
            const {
              data: { session: retrySession }
            } = await supabase.auth.getSession();
            if (retrySession) {
              console.log('[SetPasswordPage] Session found on retry after code exchange error');
              setHasValidSession(true);
              setIsExchanging(false);
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
            setError(
              'Invalid or expired invite link. Please contact your administrator for a new invite.'
            );
            setIsExchanging(false);
            return;
          }

          if (data?.session) {
            console.log('[SetPasswordPage] Session established from code exchange');
            setHasValidSession(true);
            setIsExchanging(false);
            // Clean up URL - remove code and type params
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // Check for hash tokens (fallback for non-PKCE flows)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        // If we have an access token from invite, exchange it for a session
        if (
          accessToken &&
          (hashType === 'invite' ||
            type === 'invite' ||
            hashType === 'recovery' ||
            type === 'recovery')
        ) {
          try {
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            });

            if (sessionError) {
              setError(
                'Invalid or expired invite link. Please contact your administrator for a new invite.'
              );
              setIsExchanging(false);
              return;
            }

            if (sessionData?.session) {
              setHasValidSession(true);
              setIsExchanging(false);
              // Clear the hash from URL for security
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
          } catch (err) {
            setError(
              'Invalid or expired invite link. Please contact your administrator for a new invite.'
            );
            setIsExchanging(false);
            return;
          }
        }

        // No session found from any method
        setError('No valid invite link found. Please use the link from your welcome email.');
        setIsExchanging(false);
      } catch (err) {
        console.error('[SetPasswordPage] Error during session check:', err);
        setError(
          'An error occurred while processing your invite link. Please contact your administrator for a new invite.'
        );
        setIsExchanging(false);
      }
    };

    checkSession();
  }, [searchParams]);

  // Redirect if already authenticated and password is set
  useEffect(() => {
    if (user && success) {
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    }
  }, [user, success, navigate]);

  // Show loading state while exchanging code/session
  if (isExchanging) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <Loader2 className='h-12 w-12 text-[#d4854a] mx-auto mb-4 animate-spin' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Validating Your Invite</h1>
          <p className='text-gray-600'>Please wait while we verify your invitation link...</p>
        </div>
      </div>
    );
  }

  // Show error state if no valid session
  if (!hasValidSession && error) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <AlertCircle className='h-16 w-16 text-red-500 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Invalid Invite Link</h1>
          <p className='text-gray-600 mb-4'>{error}</p>
          <button
            onClick={() => navigate('/auth')}
            className='bg-[#d4854a] text-white py-2 px-4 rounded-md hover:bg-[#c17640] transition-colors font-medium'
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);

      // Update user password using Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Update user metadata to track password was set
      await supabase.auth.updateUser({
        data: {
          password_set_at: new Date().toISOString()
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <CheckCircle className='h-16 w-16 text-green-500 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Password Set Successfully!</h1>
          <p className='text-gray-600 mb-4'>
            Your password has been set. Redirecting you to the dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Set Your Password</h1>
          <p className='text-gray-600'>
            Welcome to Vehix.ai! Please set a secure password for your account.
          </p>
        </div>

        {error && (
          <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-red-600' />
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='password' className='block text-sm font-medium text-gray-900 mb-2'>
              Password
            </label>
            <div className='relative'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Enter your password'
                className='w-full px-3 py-2 pr-10 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#d4854a] focus:border-transparent'
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
            <p className='mt-1 text-xs text-gray-500'>
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-gray-900 mb-2'
            >
              Confirm Password
            </label>
            <div className='relative'>
              <input
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm your password'
                className='w-full px-3 py-2 pr-10 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#d4854a] focus:border-transparent'
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showConfirmPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !password || !confirmPassword}
            className='w-full bg-[#d4854a] text-white py-2 px-4 rounded-md hover:bg-[#c17640] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Setting Password...
              </>
            ) : (
              'Set Password'
            )}
          </button>
        </form>

        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-600'>
            Need help?{' '}
            <a href='/support' className='text-[#d4854a] hover:text-[#c17640] font-semibold'>
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
